<script setup lang="ts">
  import { FARE_TABLE_CLUB_LEVEL, FARE_TABLE_TRAVEL_WITH } from '@/components/fare-table/types'
  import type {
    IFareOptionsByGroup,
    TFareTableClubLevel,
    IFareTableFiltersModel,
    TFareTableTravelWith
  } from '@/components/fare-table/types'
  const { t } = useI18n()
  const { isDesktop } = useBTBreakpoints()
  const checkboxIdPrefix = `fare-table-filters-${useId()}`

  const props = withDefaults(
    defineProps<{
      modelValue?: IFareTableFiltersModel
      fareOptions?: IFareOptionsByGroup
      defaultFareKeys?: string[]
    }>(),
    {
      modelValue: () => ({
        fareKeys: [],
        clubLevel: FARE_TABLE_CLUB_LEVEL.none,
        travelWith: []
      }),
      fareOptions: () => ({
        individual: [],
        interline: [],
        corporate: []
      }),
      defaultFareKeys: () => []
    }
  )

  const emits = defineEmits<{
    'update:modelValue': [value: IFareTableFiltersModel]
  }>()

  const isOpen = ref(false)
  const filtersRef = ref<HTMLElement | null>(null)
  const selectedFareKeys = ref<string[]>([])
  const selectedClubLevel = ref<TFareTableClubLevel>(FARE_TABLE_CLUB_LEVEL.none)
  const selectedTravelWith = ref<TFareTableTravelWith[]>([])

  const clubLevelOptions = computed(() => [
    { label: t('fareTable.filters.clubLevels.none'), value: FARE_TABLE_CLUB_LEVEL.none },
    { label: t('fareTable.filters.clubLevels.club'), value: FARE_TABLE_CLUB_LEVEL.club },
    { label: t('fareTable.filters.clubLevels.executive'), value: FARE_TABLE_CLUB_LEVEL.executive },
    { label: t('fareTable.filters.clubLevels.vip'), value: FARE_TABLE_CLUB_LEVEL.vip }
  ])

  const travelWithOptions = computed(
    () =>
      [
        { label: t('fareTable.filters.travelWith.baby'), value: FARE_TABLE_TRAVEL_WITH.infant },
        { label: t('fareTable.filters.travelWith.child'), value: FARE_TABLE_TRAVEL_WITH.child }
      ] as const
  )

  function cloneArray<T>(value?: T[]): T[] {
    return [...(value || [])]
  }

  const syncDraftFromModel = () => {
    selectedFareKeys.value = cloneArray(props.modelValue.fareKeys)
    selectedClubLevel.value = props.modelValue.clubLevel || FARE_TABLE_CLUB_LEVEL.none
    selectedTravelWith.value = cloneArray(props.modelValue.travelWith)
  }

  const selectedFareCount = computed(() => selectedFareKeys.value.length)
  const hasReachedFareLimit = computed(() => selectedFareCount.value >= 4)
  const allFareOptionValues = computed(() => [
    ...props.fareOptions.individual.map((option) => option.value),
    ...props.fareOptions.interline.map((option) => option.value),
    ...props.fareOptions.corporate.map((option) => option.value)
  ])
  const fallbackFareKey = computed(() => allFareOptionValues.value.find((value) => value === 'ECONOMY_MINI') || '')

  const normalizedSelectedFareKeys = computed(() => [...new Set(props.modelValue.fareKeys)])
  const normalizedDefaultFareKeys = computed(() => [...new Set(props.defaultFareKeys)])
  const areDefaultFareKeysSelected = computed(() => {
    if (normalizedSelectedFareKeys.value.length !== normalizedDefaultFareKeys.value.length) return false

    const selectedFareSet = new Set(normalizedSelectedFareKeys.value)
    return normalizedDefaultFareKeys.value.every((fareKey) => selectedFareSet.has(fareKey))
  })
  const isClubLevelDefault = computed(() => props.modelValue.clubLevel === FARE_TABLE_CLUB_LEVEL.none)
  const normalizedTravelWith = computed(() => [...new Set(props.modelValue.travelWith)])
  const isDefaultFilterState = computed(
    () => areDefaultFareKeysSelected.value && isClubLevelDefault.value && normalizedTravelWith.value.length === 0
  )
  const appliedFilterCount = computed(() => {
    if (isDefaultFilterState.value) return 0

    const clubLevelCount = isClubLevelDefault.value ? 0 : 1
    return normalizedSelectedFareKeys.value.length + normalizedTravelWith.value.length + clubLevelCount
  })
  const showFiltersAriaLabel = computed(() => {
    if (appliedFilterCount.value === 0) return t('fareTable.showFilters')

    return `${t('fareTable.showFilters')}, ${t('fareTable.filters.activeFiltersCount', { count: appliedFilterCount.value })}`
  })

  const isFareOptionDisabled = (value: string) => {
    if (selectedFareKeys.value.includes(value)) return false
    return hasReachedFareLimit.value
  }

  const saveFilters = () => {
    const normalizedFareKeys = [...selectedFareKeys.value]
    if (!normalizedFareKeys.length && fallbackFareKey.value) {
      normalizedFareKeys.push(fallbackFareKey.value)
    }

    selectedFareKeys.value = normalizedFareKeys

    emits('update:modelValue', {
      fareKeys: normalizedFareKeys,
      clubLevel: selectedClubLevel.value,
      travelWith: [...selectedTravelWith.value]
    })
    isOpen.value = false
  }

  const clearFilters = () => {
    selectedFareKeys.value = []
    selectedClubLevel.value = FARE_TABLE_CLUB_LEVEL.none
    selectedTravelWith.value = []
    saveFilters()
  }

  watch(
    () => props.modelValue,
    () => {
      if (isOpen.value) return
      syncDraftFromModel()
    },
    { deep: true, immediate: true }
  )

  watch(
    isOpen,
    (open) => {
      if (!open) return

      if (isDesktop.value) {
        nextTick(() => {
          if (!filtersRef.value) return

          const y = filtersRef.value.getBoundingClientRect().top + window.scrollY - 80
          window.scrollTo({ top: Math.max(y, 0), behavior: 'smooth' })
        })
      }

      syncDraftFromModel()
    },
    { immediate: true }
  )
</script>

<template>
  <DialogRoot v-model:open="isOpen">
    <div
      ref="filtersRef"
      class="relative inline-block"
    >
      <div class="relative inline-flex">
        <DialogTrigger as-child>
          <UiButton
            :title="$t('fareTable.showFilters')"
            :aria-label="showFiltersAriaLabel"
            variant="filter"
            icon="common/filter"
            size="md"
            :active="appliedFilterCount > 0"
          />
        </DialogTrigger>
        <span
          v-if="appliedFilterCount > 0"
          data-test="filters-badge"
          class="text-caption outline-solid pointer-events-none absolute -right-2 -top-2 inline-flex size-5 items-center justify-center rounded-full bg-bt-blue-ui text-white outline outline-2 outline-white"
          aria-hidden="true"
        >
          {{ appliedFilterCount }}
        </span>
      </div>

      <DialogPortal :disabled="true">
        <DialogOverlay
          class="fixed inset-0 z-30 bg-black/75"
          :aria-label="$t('aria.label.close')"
          @click="isOpen = false"
        />

        <DialogContent
          class="data-[state=open]:animate-contentShow fixed left-1/2 top-1/2 z-40 flex max-h-screen w-screen max-w-112 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden bg-white text-bt-blue-dark focus:outline-none sm:max-h-[65vh] sm:rounded-md md:absolute md:left-auto md:right-0 md:top-2 md:h-auto md:max-h-[80vh] md:w-144 md:max-w-144 md:origin-top-right md:-translate-x-0 md:-translate-y-0 md:transition md:duration-200 md:ease-out data-[state=closed]:md:scale-95 data-[state=open]:md:scale-100 data-[state=closed]:md:opacity-0 data-[state=open]:md:opacity-100"
        >
          <div
            class="sticky top-0 z-10 flex w-full items-center justify-between bg-white px-6 pb-6 pt-6 md:px-8 md:pt-6"
          >
            <DialogTitle
              class="flex items-center gap-3 text-h5"
              as="div"
            >
              <Icon
                name="common/filter"
                size="ml"
              />
              <span class="text-h4">{{ $t('fareTable.filters.title') }}</span>
            </DialogTitle>

            <DialogClose aria-label="Close">
              <Icon
                class="text-bt-blue-dark"
                name="common/close"
              />
            </DialogClose>
          </div>

          <div class="w-full overflow-y-auto px-6 pb-8 md:px-8">
            <section>
              <div class="text-h5">{{ $t('fareTable.filters.ticketTypeFares') }}</div>
              <UiAlert
                v-if="hasReachedFareLimit"
                variant="warning"
                :closable="false"
                class="mt-4"
              >
                <template #content>
                  {{ $t('fareTable.filters.fareLimitHint') }}
                </template>
              </UiAlert>

              <div class="mt-4">
                <div class="mb-4 text-subtitle-1">{{ $t('fareTable.filters.individualFares') }}</div>
                <div class="flex flex-col gap-y-4 md:grid md:grid-cols-2 md:gap-x-4">
                  <UiCheckbox
                    v-for="option in props.fareOptions.individual"
                    :id="`${checkboxIdPrefix}-individual-${option.value}`"
                    :key="`individual-${option.value}`"
                    v-model="selectedFareKeys"
                    :value="option.value"
                    :label="option.label"
                    :disabled="isFareOptionDisabled(option.value)"
                  />
                </div>
              </div>

              <div class="mt-6">
                <div class="mb-4 text-subtitle-1">{{ $t('fareTable.filters.interlineFares') }}</div>
                <div class="flex flex-col gap-y-4 md:grid md:grid-cols-2 md:gap-x-4">
                  <UiCheckbox
                    v-for="option in props.fareOptions.interline"
                    :id="`${checkboxIdPrefix}-interline-${option.value}`"
                    :key="`interline-${option.value}`"
                    v-model="selectedFareKeys"
                    :value="option.value"
                    :label="option.label"
                    :disabled="isFareOptionDisabled(option.value)"
                  />
                </div>
              </div>

              <div class="mt-6">
                <div class="mb-4 text-subtitle-1">{{ $t('fareTable.filters.corporateFares') }}</div>
                <div class="flex flex-col gap-y-4 md:grid md:grid-cols-2 md:gap-x-4">
                  <UiCheckbox
                    v-for="option in props.fareOptions.corporate"
                    :id="`${checkboxIdPrefix}-corporate-${option.value}`"
                    :key="`corporate-${option.value}`"
                    v-model="selectedFareKeys"
                    :value="option.value"
                    :label="option.label"
                    :disabled="isFareOptionDisabled(option.value)"
                  />
                </div>
              </div>
            </section>

            <section class="mt-4 border-t border-dashed border-divider p-4">
              <div class="mb-4 text-h5">{{ $t('fareTable.filters.clubLevelsTitle') }}</div>
              <UiRadioGroup
                v-model="selectedClubLevel"
                name="fare-table-club-level"
                direction="row"
                variant="primary"
                :items="clubLevelOptions"
                class="flex flex-col !gap-y-4 md:grid md:grid-cols-2 md:gap-x-4"
              />
            </section>

            <section class="mt-4 border-t border-dashed border-divider p-4">
              <div class="mb-4 text-h5">{{ $t('fareTable.filters.travelWithTitle') }}</div>
              <div class="flex flex-col !gap-y-4 md:grid md:grid-cols-2 md:gap-x-4">
                <UiCheckbox
                  v-for="option in travelWithOptions"
                  :id="`${checkboxIdPrefix}-travel-${option.value}`"
                  :key="`travel-${option.value}`"
                  v-model="selectedTravelWith"
                  :value="option.value"
                  :label="option.label"
                />
              </div>
            </section>

            <div class="mt-8 flex flex-col items-center gap-4 md:flex-row">
              <UiButton
                :title="$t('save')"
                size="lg"
                variant="primary"
                class="w-full"
                @click="saveFilters"
              />
              <UiButton
                :title="$t('clear')"
                variant="link"
                class="w-full"
                size="lg"
                @click="clearFilters"
              />
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </div>
  </DialogRoot>
</template>
